/**
 * Навигация админки. Подписи для общепита при тех же маршрутах — только
 * `HOSPITALITY_MODULE_LABEL_OVERRIDES` и `getNavGroups(..., productVertical)`;
 * не дублировать страницы и не переименовывать `moduleKey` / пути ради текста.
 */
import {
  AlertTriangle,
  Bell,
  Briefcase,
  CheckSquare,
  ClipboardList,
  Clock,
  CreditCard,
  DollarSign,
  Wallet,
  Handshake,
  LayoutDashboard,
  MapPin,
  Package,
  Settings,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { isCashMetricsUiVisible } from '@/lib/cash-metrics-ui';
import { isConsumablesUiVisible, isSuperAdminUi, SUPER_ADMIN_HIDDEN_BADGE } from '@/lib/super-admin-ui';

export type AppRole = 'admin' | 'manager' | 'employee';
export type AccessScope = 'self' | 'location' | 'organization';
export type ProductVertical = 'pvz' | 'hospitality';

export interface ModuleAccess {
  visible: boolean;
  actions: string[];
  scope: AccessScope;
}

export interface AppUser {
  id: number;
  email: string;
  full_name: string;
  last_name?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  phone?: string | null;
  role: AppRole;
  /** Matches role when bootstrap is loaded (three base roles). */
  effective_role?: AppRole;
  support_global_access?: boolean;
  needs_onboarding?: boolean;
  /** Организация: ПВЗ (база) или общепит (надстройка). */
  product_vertical?: ProductVertical;
  /** True если users.id === organizations.owner_user_id (создатель / владелец). */
  is_organization_owner?: boolean;
  organization_id: number;
  organization?: {
    id: number;
    name: string;
    legal_name?: string | null;
    inn?: string | null;
    phone?: string | null;
    email?: string | null;
    /** Effective: trial | active | expired (soft; expired does not block API). */
    subscription_status?: 'trial' | 'active' | 'expired' | string | null;
    trial_started_at?: string | null;
    trial_ends_at?: string | null;
    trial_days_left?: number | null;
  } | null;
  employee_id?: number | null;
  location_id?: number | null;
  location_name?: string | null;
}

export interface AppLocation {
  id: number;
  name: string;
  city?: string | null;
  address?: string | null;
}

export interface AccessState {
  role: AppRole;
  /** Effective base role (custom role template); same as API access.base_role when bootstrap is loaded. */
  base_role?: AppRole;
  modules: Record<string, ModuleAccess>;
  allowed_location_ids: number[];
  primary_location_id?: number | null;
  scope: AccessScope;
}

export interface AppBootstrap {
  user: AppUser;
  access: AccessState;
  available_locations: AppLocation[];
}

export interface NavGroup {
  label: string;
  items: Array<{
    href: string;
    icon: LucideIcon;
    label: string;
    moduleKey: string;
    badge?: string;
  }>;
}

const MODULE_LABELS: Record<string, string> = {
  dashboard: 'Аналитика',
  analytics: 'Аналитика',
  cash: 'Касса и выручка',
  wallet: 'Кошелёк',
  counterparties: 'Поставщики',
  locations: 'Точки выдачи',
  employees: 'Сотрудники',
  shifts: 'Смены',
  tasks: 'Задачи',
  checklists: 'Чек-листы',
  consumables: 'Снабжение',
  incidents: 'Инциденты',
  chat: 'Чат',
  hr: 'Кадры',
  documents: 'Документы',
  instructions: 'Инструкции',
  settings: 'Настройки',
  roles: 'Роли и доступы',
  billing: 'Подписка',
  notifications: 'Уведомления',
};

/** Подписи разделов для общепита при той же логике маршрутов (не дублируем страницы). */
const HOSPITALITY_MODULE_LABEL_OVERRIDES: Partial<Record<string, string>> = {
  locations: 'Заведения',
  consumables: 'Снабжение',
};

const NAV_PARENT_BY_MODULE: Record<string, string> = {
  documents: 'hr',
  instructions: 'hr',
  roles: 'settings',
};

const PRIMARY_NAV_HREF_BY_MODULE: Record<string, string> = {
  dashboard: '/',
  analytics: '/',
  cash: '/cash',
  wallet: '/wallet',
  counterparties: '/counterparties',
  locations: '/locations',
  employees: '/employees',
  shifts: '/shifts',
  tasks: '/tasks',
  checklists: '/checklists',
  consumables: '/consumables',
  incidents: '/incidents',
  chat: '/chat',
  hr: '/hr/positions',
  settings: '/settings',
  billing: '/billing',
  notifications: '/notifications',
};

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Операции',
    items: [
      { href: '/', icon: LayoutDashboard, label: 'Аналитика', moduleKey: 'dashboard' },
      { href: '/wallet', icon: Wallet, label: 'Кошелёк', moduleKey: 'wallet' },
      { href: '/counterparties', icon: Handshake, label: 'Поставщики', moduleKey: 'counterparties' },
    ],
  },
  {
    label: 'Основное',
    items: [
      { href: '/locations', icon: MapPin, label: 'Точки выдачи', moduleKey: 'locations' },
      { href: '/employees', icon: Users, label: 'Сотрудники', moduleKey: 'employees' },
      { href: '/hr/positions', icon: Briefcase, label: 'Кадры', moduleKey: 'hr' },
    ],
  },
  {
    label: 'Контроль',
    items: [
      { href: '/shifts', icon: Clock, label: 'Смены', moduleKey: 'shifts' },
      { href: '/checklists', icon: ClipboardList, label: 'Чек-листы', moduleKey: 'checklists' },
      { href: '/incidents', icon: AlertTriangle, label: 'Инциденты', moduleKey: 'incidents' },
      { href: '/tasks', icon: CheckSquare, label: 'Задачи', moduleKey: 'tasks' },
      { href: '/cash', icon: DollarSign, label: 'Касса и выручка', moduleKey: 'cash' },
      { href: '/consumables', icon: Package, label: 'Снабжение', moduleKey: 'consumables' },
    ],
  },
  {
    label: 'Система',
    items: [
      { href: '/settings', icon: Settings, label: 'Настройки', moduleKey: 'settings' },
      { href: '/billing', icon: CreditCard, label: 'Подписка', moduleKey: 'billing' },
    ],
  },
];

export function canAccessModule(access: AccessState | null | undefined, moduleKey: string, action = 'view') {
  if (!access) return false;
  const moduleAccess = access.modules[moduleKey];
  if (!moduleAccess) return false;
  if (action === 'view') return moduleAccess.visible && moduleAccess.actions.includes('view');
  return moduleAccess.actions.includes(action);
}

export function getNavGroups(
  access: AccessState | null | undefined,
  productVertical: ProductVertical = 'pvz',
  email?: string | null,
  opts?: {
    isOrganizationOwner?: boolean;
    userRole?: AppRole;
    userEffectiveRole?: AppRole;
  },
) {
  if (!access) return [];
  const labelOverride =
    productVertical === 'hospitality' ? HOSPITALITY_MODULE_LABEL_OVERRIDES : null;
  const isOrganizationOwner = opts?.isOrganizationOwner ?? false;
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => {
        if (!canAccessModule(access, item.moduleKey)) return false;
        if (item.moduleKey === 'cash' && productVertical === 'pvz') return false;
        if (item.moduleKey === 'cash' && !isCashMetricsUiVisible(email)) return false;
        if (item.moduleKey === 'consumables' && !isConsumablesUiVisible(email)) return false;
        if (item.moduleKey === 'wallet' && productVertical === 'pvz') return false;
        if (
          item.moduleKey === 'wallet' &&
          !isWalletWebAdminReadUi(
            {
              product_vertical: productVertical,
              is_organization_owner: isOrganizationOwner,
              role: opts?.userRole,
              effective_role: opts?.userEffectiveRole,
            },
            access,
          )
        ) {
          return false;
        }
        if (item.moduleKey === 'counterparties' && productVertical === 'pvz') return false;
        if (
          item.moduleKey === 'counterparties' &&
          !isCounterpartyWebAdminReadUi(
            {
              product_vertical: productVertical,
              is_organization_owner: isOrganizationOwner,
              role: opts?.userRole,
              effective_role: opts?.userEffectiveRole,
            },
            access,
          )
        ) {
          return false;
        }
        return true;
      })
      .map((item) => {
        const label =
          labelOverride?.[item.moduleKey] ??
          MODULE_LABELS[item.moduleKey] ??
          item.label;
        if (
          isSuperAdminUi(email) &&
          (item.moduleKey === 'cash' || item.moduleKey === 'consumables')
        ) {
          return { ...item, label, badge: item.badge ?? SUPER_ADMIN_HIDDEN_BADGE };
        }
        return { ...item, label };
      }),
  })).filter((group) => group.items.length > 0);
}

export function getRoleLabel(
  role?: string,
  opts?: { is_organization_owner?: boolean },
) {
  if (role === 'admin') {
    if (opts?.is_organization_owner === false) return 'Администратор';
    return 'Владелец';
  }
  if (role === 'manager') return 'Менеджер';
  if (role === 'employee') return 'Сотрудник';
  return role || 'Пользователь';
}

/** Критические действия в админке (орг-профиль, удаление точек и т.п.). */
export function isWebOrganizationOwner(user: AppUser | null | undefined) {
  return Boolean(user?.is_organization_owner);
}

/** Удаление wallet-шаблона навсегда — только владелец или admin (не manager). */
export function canWalletTemplateHardDelete(
  user?: Pick<AppUser, 'is_organization_owner' | 'role' | 'effective_role'> | null,
): boolean {
  if (!user) return false;
  if (isWebOrganizationOwner(user)) return true;
  const eff = (user.effective_role ?? user.role) as string | undefined;
  return eff === 'admin';
}

/** WebAdmin «Кошелёк» — мутации: владелец, admin или manager с wallet.manage. */
export function isWalletWebAdminManageUi(
  user?: Pick<AppUser, 'is_organization_owner' | 'product_vertical' | 'role' | 'effective_role'> | null,
  access?: AccessState | null,
): boolean {
  if (user?.product_vertical !== 'hospitality') return false;
  if (isWebOrganizationOwner(user)) return true;
  const eff = (user?.effective_role ?? user?.role) as string | undefined;
  if (eff === 'admin') return true;
  if (access && canAccessModule(access, 'wallet', 'manage')) return true;
  return false;
}

/** WebAdmin «Кошелёк» — просмотр: manage или manager с wallet.review. */
export function isWalletWebAdminReadUi(
  user?: Pick<AppUser, 'is_organization_owner' | 'product_vertical' | 'role' | 'effective_role'> | null,
  access?: AccessState | null,
): boolean {
  if (isWalletWebAdminManageUi(user, access)) return true;
  if (user?.product_vertical !== 'hospitality') return false;
  const eff = (user?.effective_role ?? user?.role) as string | undefined;
  if (eff === 'manager' && access && canAccessModule(access, 'wallet', 'review')) return true;
  return false;
}

/** Удаление counterparty-шаблона навсегда — только владелец или admin (не manager). */
export function canCounterpartyTemplateHardDelete(
  user?: Pick<AppUser, 'is_organization_owner' | 'role' | 'effective_role'> | null,
): boolean {
  if (!user) return false;
  if (isWebOrganizationOwner(user)) return true;
  const eff = (user.effective_role ?? user.role) as string | undefined;
  return eff === 'admin';
}

/** WebAdmin «Контрагенты» — мутации: владелец, admin или manager с counterparties.manage. */
export function isCounterpartyWebAdminManageUi(
  user?: Pick<AppUser, 'is_organization_owner' | 'product_vertical' | 'role' | 'effective_role'> | null,
  access?: AccessState | null,
): boolean {
  if (user?.product_vertical !== 'hospitality') return false;
  if (isWebOrganizationOwner(user)) return true;
  const eff = (user?.effective_role ?? user?.role) as string | undefined;
  if (eff === 'admin') return true;
  if (access && canAccessModule(access, 'counterparties', 'manage')) return true;
  return false;
}

/** WebAdmin «Контрагенты» — просмотр: manage или manager с counterparties.review. */
export function isCounterpartyWebAdminReadUi(
  user?: Pick<AppUser, 'is_organization_owner' | 'product_vertical' | 'role' | 'effective_role'> | null,
  access?: AccessState | null,
): boolean {
  if (isCounterpartyWebAdminManageUi(user, access)) return true;
  if (user?.product_vertical !== 'hospitality') return false;
  const eff = (user?.effective_role ?? user?.role) as string | undefined;
  if (eff === 'manager' && access && canAccessModule(access, 'counterparties', 'review')) return true;
  return false;
}

/** @deprecated Используйте isWalletWebAdminManageUi */
export function isWalletWebAdminUi(
  user?: Parameters<typeof isWalletWebAdminManageUi>[0],
  access?: AccessState | null,
): boolean {
  return isWalletWebAdminManageUi(user, access);
}

/** Login / shell: admin or manager in effective terms (custom role base included). */
export function isWebAdminEligible(user: { role?: string; effective_role?: string | null } | null | undefined) {
  if (!user) return false;
  const eff = (user.effective_role ?? user.role) as string | undefined;
  return eff === 'admin' || eff === 'manager';
}

function modulesForRole(role: AppRole): Record<string, ModuleAccess> {
  if (role === 'admin') {
    return {
      dashboard: { visible: true, actions: ['view'], scope: 'organization' },
      analytics: { visible: true, actions: ['view'], scope: 'organization' },
      cash: { visible: true, actions: ['view', 'review', 'export'], scope: 'organization' },
      wallet: { visible: true, actions: ['view', 'scan', 'review', 'export', 'manage'], scope: 'organization' },
      counterparties: { visible: true, actions: ['view', 'scan', 'review', 'export', 'manage'], scope: 'organization' },
      locations: { visible: true, actions: ['view', 'create', 'edit', 'delete'], scope: 'organization' },
      employees: { visible: true, actions: ['view', 'create', 'edit', 'delete'], scope: 'organization' },
      shifts: { visible: true, actions: ['view', 'create', 'edit'], scope: 'organization' },
      tasks: { visible: true, actions: ['view', 'create', 'edit', 'assign'], scope: 'organization' },
      checklists: { visible: true, actions: ['view', 'create', 'edit'], scope: 'organization' },
      consumables: {
        visible: true,
        actions: ['view', 'approve', 'reject', 'complete', 'manage_catalog'],
        scope: 'organization',
      },
      incidents: { visible: true, actions: ['view', 'edit', 'resolve'], scope: 'organization' },
      chat: { visible: true, actions: ['view'], scope: 'organization' },
      hr: { visible: true, actions: ['view'], scope: 'organization' },
      documents: { visible: true, actions: ['view', 'create', 'edit', 'delete', 'acknowledge'], scope: 'organization' },
      instructions: { visible: true, actions: ['view', 'edit'], scope: 'organization' },
      settings: { visible: true, actions: ['view', 'edit'], scope: 'organization' },
      roles: { visible: true, actions: ['view', 'edit'], scope: 'organization' },
      billing: { visible: true, actions: ['view'], scope: 'organization' },
      audit: { visible: true, actions: ['view'], scope: 'organization' },
    };
  }

  return {
    dashboard: { visible: true, actions: ['view'], scope: 'location' },
    analytics: { visible: true, actions: ['view'], scope: 'location' },
    cash: { visible: true, actions: ['view', 'review', 'export'], scope: 'location' },
    wallet: { visible: true, actions: ['view', 'scan'], scope: 'self' },
    counterparties: { visible: true, actions: ['view', 'scan'], scope: 'self' },
    locations: { visible: true, actions: ['view'], scope: 'location' },
    employees: { visible: true, actions: ['view'], scope: 'location' },
    shifts: { visible: true, actions: ['view', 'create', 'edit'], scope: 'location' },
    tasks: { visible: true, actions: ['view', 'create', 'edit', 'assign'], scope: 'location' },
    checklists: { visible: true, actions: ['view', 'create', 'edit'], scope: 'location' },
    consumables: {
      visible: true,
      actions: ['view', 'approve', 'reject', 'complete', 'manage_catalog'],
      scope: 'location',
    },
    incidents: { visible: true, actions: ['view', 'edit', 'resolve'], scope: 'location' },
    chat: { visible: true, actions: ['view'], scope: 'location' },
    hr: { visible: true, actions: ['view'], scope: 'location' },
    documents: { visible: true, actions: ['view', 'acknowledge'], scope: 'location' },
    instructions: { visible: true, actions: ['view'], scope: 'location' },
    settings: { visible: true, actions: ['view'], scope: 'location' },
    roles: { visible: false, actions: [], scope: 'organization' },
    billing: { visible: false, actions: [], scope: 'organization' },
    audit: { visible: false, actions: [], scope: 'organization' },
  };
}

export function buildFallbackBootstrap(user: AppUser): AppBootstrap {
  const primaryLocationId = user.location_id ?? null;
  const allowedLocationIds = primaryLocationId ? [primaryLocationId] : [];
  const eff = (user.effective_role ?? user.role) as AppRole;
  return {
    user,
    access: {
      role: user.role,
      base_role: eff,
      modules: modulesForRole(eff),
      allowed_location_ids: allowedLocationIds,
      primary_location_id: primaryLocationId,
      scope: eff === 'admin' ? 'organization' : 'location',
    },
    available_locations: [],
  };
}

export function getModuleForPath(pathname?: string | null) {
  if (!pathname || pathname === '/') return 'dashboard';
  if (pathname.startsWith('/analytics')) return 'dashboard';
  if (pathname.startsWith('/cash')) return 'cash';
  if (pathname.startsWith('/wallet')) return 'wallet';
  if (pathname.startsWith('/counterparties')) return 'counterparties';
  if (pathname.startsWith('/compliance')) return 'dashboard';
  if (pathname.startsWith('/reports/financial')) return 'cash';
  if (pathname.startsWith('/reports')) return 'dashboard';
  if (pathname.startsWith('/ai')) return 'dashboard';
  if (pathname.startsWith('/locations')) return 'locations';
  if (pathname.startsWith('/employees')) return 'employees';
  if (pathname.startsWith('/shifts')) return 'shifts';
  if (pathname.startsWith('/tasks')) return 'tasks';
  if (pathname.startsWith('/checklists')) return 'checklists';
  if (pathname.startsWith('/schedules')) return 'checklists';
  if (pathname.startsWith('/consumables')) return 'consumables';
  if (pathname.startsWith('/incidents')) return 'incidents';
  if (pathname.startsWith('/notifications')) return 'notifications';
  if (pathname.startsWith('/platform')) return 'chat';
  if (pathname.startsWith('/support')) return 'chat';
  if (pathname.startsWith('/chat')) return 'chat';
  if (pathname.startsWith('/hr/instructions')) return 'instructions';
  if (pathname.startsWith('/hr/documents')) return 'documents';
  if (pathname.startsWith('/documents')) return 'documents';
  if (pathname.startsWith('/settings/instructions')) return 'instructions';
  if (pathname.startsWith('/instructions')) return 'instructions';
  if (pathname.startsWith('/billing')) return 'billing';
  if (pathname.startsWith('/settings/roles')) return 'roles';
  if (pathname.startsWith('/settings/compliance')) return 'settings';
  if (pathname.startsWith('/audit')) return 'settings';
  if (pathname.startsWith('/hr')) return 'hr';
  if (pathname.startsWith('/settings')) return 'settings';
  return 'dashboard';
}

export function getActiveNavModuleForPath(pathname?: string | null) {
  const moduleKey = getModuleForPath(pathname);
  return NAV_PARENT_BY_MODULE[moduleKey] || moduleKey;
}

export function getActiveNavHref(pathname?: string | null) {
  if (!pathname || pathname === '/') return '/';
  if (pathname.startsWith('/platform')) return '/platform';
  if (pathname.startsWith('/support')) return '/support';

  const currentModule = getModuleForPath(pathname);
  const parentModule = NAV_PARENT_BY_MODULE[currentModule];
  if (parentModule) {
    return PRIMARY_NAV_HREF_BY_MODULE[parentModule] || '/';
  }

  const primaryHref = PRIMARY_NAV_HREF_BY_MODULE[currentModule];
  if (primaryHref) {
    return primaryHref;
  }

  const matchingItem = NAV_GROUPS
    .flatMap((group) => group.items)
    .filter((item) => item.href !== '/' && pathname.startsWith(item.href))
    .sort((a, b) => b.href.length - a.href.length)[0];

  return matchingItem?.href || '/';
}

export function getModuleLabel(moduleKey?: string | null, productVertical: ProductVertical = 'pvz') {
  if (!moduleKey) return null;
  if (productVertical === 'hospitality') {
    const o = HOSPITALITY_MODULE_LABEL_OVERRIDES[moduleKey];
    if (o) return o;
  }
  return MODULE_LABELS[moduleKey] || null;
}

/** Compat for shared chat sheets imported via `@/lib/access` in web-admin. */
export function isPwaAdminRoleUI(role?: string | null): role is 'admin' {
  return role === 'admin';
}

export function getPwaDashboardEffectiveRole(
  _access: AccessState | null | undefined,
  user?: { role?: string | null } | null
): string | null | undefined {
  return user?.role;
}

export function isPwaManagerOrOwnerContext(
  _access: AccessState | null | undefined,
  user?: { role?: string | null; is_organization_owner?: boolean | null } | null
): boolean {
  if (user?.is_organization_owner) return true;
  const r = user?.role;
  return r === 'admin' || r === 'manager';
}
